import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import PersonIcon from '@mui/icons-material/Person';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';

const ManagementAccountPage: React.FC = () => {
  const { managementAccount, managementToken, refreshAccount } = useManagementAuth();
  const [isEditBasic, setIsEditBasic] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (managementAccount) {
      setName(managementAccount.name);
      setPhone(managementAccount.phone || '');
    }
  }, [managementAccount]);

  const handleSaveBasic = async () => {
    if (!managementToken) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.managementAuth.updateMe(managementToken, { name, phone });
      await refreshAccount();
      setIsEditBasic(false);
      setMessage({ type: 'success', text: '基本情報を更新しました' });
    } catch {
      setMessage({ type: 'error', text: '更新に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelBasic = () => {
    if (managementAccount) {
      setName(managementAccount.name);
      setPhone(managementAccount.phone || '');
    }
    setIsEditBasic(false);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
        アカウント情報
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* 基本情報 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<PersonIcon color="primary" />}
          title="基本情報"
          action={
            !isEditBasic ? (
              <Button variant="outlined" size="small" onClick={() => setIsEditBasic(true)}>
                編集
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={handleCancelBasic} disabled={saving}>
                  キャンセル
                </Button>
                <Button variant="contained" size="small" onClick={handleSaveBasic} disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : '保存'}
                </Button>
              </Box>
            )
          }
        />
        <CardContent>
          {!isEditBasic ? (
            <Box>
              <InfoRow label="氏名" value={managementAccount?.name} />
              <InfoRow label="電話番号" value={managementAccount?.phone} />
            </Box>
          ) : (
            <Box>
              <TextField
                label="氏名" fullWidth margin="normal"
                value={name} onChange={(e) => setName(e.target.value)}
              />
              <TextField
                label="電話番号" fullWidth margin="normal"
                value={phone} onChange={(e) => setPhone(e.target.value)}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body1">{value || '-'}</Typography>
  </Box>
);

export default ManagementAccountPage;
